import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { PaymentDetailsForm } from "../components/PaymentDetailsForm";
import { useScrollIntoViewWhenReady } from "../hooks/useScrollIntoViewWhenReady";
import { useScrollToTopWhenLoading } from "../hooks/useScrollToTopWhenLoading";
import { recaptchaHeaders } from "../lib/recaptcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scheduleId = searchParams.get("schedule_id");

  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const formSectionRef = useRef(null);
  useScrollToTopWhenLoading(loading);
  useScrollIntoViewWhenReady(formSectionRef, !loading && !!schedule, { mobileOnly: true });

  useEffect(() => {
    if (scheduleId) {
      fetchSchedule();
    } else {
      navigate("/");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, navigate]);

  const fetchSchedule = async () => {
    try {
      const res = await axios.get(`${API}/schedules/${scheduleId}`);
      setSchedule(res.data);
    } catch {
      toast.error("Class not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  // Set once the booking row exists, so the catch below can tell a booking
  // failure apart from a payment failure.
  const bookingIdRef = useRef(null);

  const handleFormSubmit = async (formData) => {
    setSubmitting(true);
    try {
      const basePrice = schedule?.price || schedule?.dance_class?.price || 0;
      const discountPct = schedule?.discount_percentage || 0;
      const price = discountPct > 0 ? (schedule?.discounted_price ?? basePrice) : basePrice;

      // 1. Create booking
      const rcHeaders = await recaptchaHeaders("submit_booking");
      const bookingRes = await axios.post(`${API}/bookings`, {
        guest_first_name: formData.guest_first_name,
        guest_last_name: formData.guest_last_name,
        guest_name: formData.guest_name,
        guest_email: formData.guest_email,
        guest_phone: formData.guest_phone,
        class_schedule_id: scheduleId,
        booking_type: "class",
        total_amount: price,
        source: searchParams.get("utm_source") ? "wordpress" : "direct",
        utm_source: searchParams.get("utm_source"),
        utm_medium: searchParams.get("utm_medium"),
        utm_campaign: searchParams.get("utm_campaign"),
      }, { headers: rcHeaders });

      const booking = bookingRes.data;
      bookingIdRef.current = booking.id;

      // 2. Initialise STK push
      const paymentRes = await axios.post(`${API}/payments/initialize`, {
        booking_id: booking.id,
        amount: booking.total_amount,
        phone_number: formData.guest_phone,
      });

      if (!paymentRes.data.status) throw new Error("Payment initialization failed");

      const reference = paymentRes.data.reference;

      if (paymentRes.data.mock) {
        // Mock mode – auto-confirm and go straight to success
        await axios.get(`${API}/payments/verify/${reference}`);
        navigate(`/payment-success?booking=${booking.booking_number}&reference=${reference}`);
        return;
      }

      // 3. Navigate to the processing/waiting page
      navigate("/payment-processing", {
        state: {
          bookingNumber: booking.booking_number,
          bookingId: booking.id,
          reference,
          phone: formData.guest_phone,
          amount: booking.total_amount,
          customerName: formData.guest_name || `${formData.guest_first_name || ""} ${formData.guest_last_name || ""}`.trim(),
          email: formData.guest_email,
        },
      });
    } catch (error) {
      // The booking is committed before payment is attempted, so a failure here
      // is almost always the payment step. Saying "booking failed" sends people
      // back to re-submit the form, which creates a duplicate.
      const stage = bookingIdRef.current ? "payment" : "booking";
      toast.error(
        error.response?.data?.detail ||
          (stage === "payment"
            ? "Couldn't start the M-Pesa payment. Please try again — your booking is saved."
            : "Failed to create booking. Please try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container-empire flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
      </div>
    );
  }

  const danceClass = schedule?.dance_class || {};
  const displayTitle = schedule?.title || danceClass.name || "Dance Class";
  const instructor = schedule?.instructor || {};
  const studio = schedule?.studio || {};
  const startTime = schedule?.start_datetime ? parseISO(schedule.start_datetime) : new Date();
  const basePrice = schedule?.price || danceClass.price || 0;
  const discountPct = schedule?.discount_percentage || 0;
  const price = discountPct > 0 ? (schedule?.discounted_price ?? basePrice) : basePrice;

  return (
    <div className="container-empire max-w-4xl" data-testid="booking-page">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="mb-6 text-neutral-400 hover:text-white"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Schedule
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:items-stretch">
        {/* Booking Summary */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="h-full"
        >
          <Card className="card-empire-featured overflow-hidden flex flex-col h-full">
            {/* Image grows to fill available height */}
            <div className="relative flex-1 min-h-48">
              <img
                src={schedule.image || danceClass.image || "https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=800"}
                alt={displayTitle}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="font-cookie text-2xl text-empire-gold">You're booking</span>
                <h2 className="text-2xl font-bold text-white">{displayTitle}</h2>
              </div>
            </div>

            <CardContent className="p-6 space-y-4 flex-shrink-0">
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-xl bg-empire-gold/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-empire-gold" />
                </div>
                <div>
                  <p className="font-semibold">{format(startTime, "EEEE, MMMM d, yyyy")}</p>
                  <p className="text-sm text-neutral-400">{format(startTime, "h:mm a")}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-xl bg-empire-gold/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-empire-gold" />
                </div>
                <div>
                  <p className="font-semibold">{danceClass.duration_minutes || 60} minutes</p>
                  <p className="text-sm text-neutral-400">
                    {danceClass.difficulty_level?.replace("_", " ") || "All Levels"}
                  </p>
                </div>
              </div>

              {instructor.name && (
                <div className="flex items-center gap-3 text-white">
                  <div className="w-10 h-10 rounded-xl bg-empire-gold/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-empire-gold" />
                  </div>
                  <div>
                    <p className="font-semibold">{instructor.name}</p>
                    <p className="text-sm text-neutral-400">Instructor</p>
                  </div>
                </div>
              )}

              {studio.name && (
                <div className="flex items-center gap-3 text-white">
                  <div className="w-10 h-10 rounded-xl bg-empire-gold/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-empire-gold" />
                  </div>
                  <div>
                    <p className="font-semibold">{studio.name}</p>
                    <p className="text-sm text-neutral-400">{studio.location}</p>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-neutral-800">
                <div className="flex items-center justify-between">
                  <span className="text-neutral-400">Total Amount</span>
                  <div className="text-right">
                    {discountPct > 0 && (
                      <span className="block text-sm text-neutral-500 line-through">
                        KES {basePrice.toLocaleString()}
                      </span>
                    )}
                    <div className="flex items-center gap-2 justify-end">
                      {discountPct > 0 && (
                        <span className="text-xs font-bold text-green-400 bg-green-500/15 border border-green-500/30 px-1.5 py-0.5 rounded">
                          -{Math.round(discountPct)}%
                        </span>
                      )}
                      <span className="text-3xl font-bold text-empire-gold">
                        KES {price.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Payment Details Form — scroll-mt aligns with fixed header when scrolling into view on mobile */}
        <motion.div
          ref={formSectionRef}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="h-full scroll-mt-24 md:scroll-mt-0"
          data-testid="booking-form-section"
        >
          <Card className="card-empire h-full flex flex-col">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-empire-gold" />
                Your Details
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1">
              <PaymentDetailsForm
                onSubmit={handleFormSubmit}
                submitting={submitting}
                amount={price}
              />
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
